import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { NtIcon } from './NotionUI';

/**
 * Pulsante che apre il menu per assegnare uno o più clienti a un gruppo:
 * elenco dei gruppi esistenti, creazione di un gruppo nuovo e, se serve,
 * "Rimuovi dal gruppo". Il menu sta nel body, quindi non viene tagliato dalle tabelle.
 *
 * onPick(groupId | null) · onCreate(name) devono restituire una Promise (o nulla):
 * il menu si chiude quando finiscono; se falliscono resta aperto.
 */
function CustomerGroupMenu({ groups, currentGroupId = null, showRemove, onPick, onCreate, className = '', title, children }) {
  const { t } = useLanguage();
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    setName('');
  }, []);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = 260;
    const below = window.innerHeight - rect.bottom - 8;
    const above = rect.top - 8;
    const up = below < 260 && above > below;
    setPlace({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      top: up ? undefined : rect.bottom + 4,
      bottom: up ? window.innerHeight - rect.top + 4 : undefined,
      width,
      maxHeight: Math.max(160, up ? above : below),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) close();
    };
    const onKey = (e) => { if (e.key === 'Escape') { close(); buttonRef.current?.focus(); } };
    const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) close(); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  useEffect(() => {
    if (open && place) inputRef.current?.focus({ preventScroll: true });
  }, [open, place]);

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      close();
    } catch {
      // l'errore lo mostra la pagina; il menu resta aperto per riprovare
    } finally {
      setBusy(false);
    }
  };

  const submitNew = (e) => {
    e.preventDefault();
    const value = name.trim();
    if (value) run(() => onCreate(value));
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={className}
        title={title}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        {children}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="cg-menu"
          role="menu"
          style={place ? { ...place, visibility: 'visible' } : { visibility: 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          <form className="cg-new" onSubmit={submitNew}>
            <input
              ref={inputRef}
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('customers.groupNewPlaceholder')}
              aria-label={t('customers.groupNewPlaceholder')}
              disabled={busy}
            />
            <button type="submit" className="cg-new-btn" disabled={busy || !name.trim()} title={t('customers.groupCreateAssign')}>
              <NtIcon name="plus" size={14} />
            </button>
          </form>
          <div className="cg-list">
            {groups.length === 0 && <p className="cg-empty">{t('customers.groupNone')}</p>}
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                role="menuitem"
                className={'cg-item' + (g.id === currentGroupId ? ' cg-item-current' : '')}
                disabled={busy}
                onClick={() => run(() => onPick(g.id))}
              >
                <i className="cg-dot" style={{ background: g.color }} aria-hidden="true" />
                <span className="cg-item-name">{g.name}</span>
                {g.id === currentGroupId && <span aria-hidden="true">✓</span>}
              </button>
            ))}
          </div>
          {(showRemove ?? currentGroupId !== null) && (
            <button type="button" role="menuitem" className="cg-item cg-item-remove" disabled={busy} onClick={() => run(() => onPick(null))}>
              {t('customers.groupRemove')}
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

export default CustomerGroupMenu;
